import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { LockKeyhole, Settings2, ShieldAlert } from 'lucide-react';

export const AIEngineeringControlPlaneCard = ({
  config,
  settingsForm,
  onSettingsChange,
  onSaveSettings,
  savingSettings,
  credentialValue,
  onCredentialChange,
  onSaveCredential,
  onClearCredential,
  savingCredential,
}) => {
  const canManage = Boolean(config?.permissions?.['ai_engineering.manage_provider_settings']?.allowed);
  const settingsDisabled = savingSettings || !canManage;
  const credentialDisabled = savingCredential || !canManage;
  const activeSource = config?.active_credential_source || 'none';
  const hasIssues = Boolean(config?.readiness_issues?.length);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="ai-engineering-control-plane-card">
      <CardHeader className="space-y-3 pb-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-950"><Settings2 className="h-4 w-4 text-slate-500" /> Control plane V2</CardTitle>
            <CardDescription className="mt-1 text-xs text-slate-600">Provider, modelo, timeout, retries e política de credenciais. Analyze Real e Suggest Fix Real ficam disponíveis quando houver provider, modelo e credencial válidos; Apply to Sandbox continua desligado.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" className="rounded-sm border-slate-300 bg-white text-slate-700" data-testid="ai-engineering-active-source-badge">Fonte activa: {activeSource}</Badge>
            <Badge variant="outline" className="rounded-sm border-slate-300 bg-white text-slate-700" data-testid="ai-engineering-fallback-state-badge">Fallback: {config?.fallback_enabled ? 'ON' : 'OFF'}</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="ai-engineering-control-plane-readiness">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Readiness</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{hasIssues ? 'Bloqueado de forma controlada' : 'Analyze + Suggest Fix prontos'}</p>
            <p className="mt-1 text-xs text-slate-500">Sem Apply to Sandbox nesta fase.</p>
          </div>
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="ai-engineering-control-plane-env-state">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Credencial env</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{config?.env_credential_configured ? 'Disponível' : 'Ausente'}</p>
            <p className="mt-1 text-xs text-slate-500">{config?.env_credential_key_name || 'Nenhuma variável detectada'}</p>
          </div>
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="ai-engineering-control-plane-override-state">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Override seguro</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{config?.secure_override?.configured ? 'Configurado' : 'Não configurado'}</p>
            <p className="mt-1 text-xs text-slate-500">{config?.secure_override?.masked_value || 'UI write-only; valor nunca regressa ao frontend'}</p>
          </div>
        </div>

        {hasIssues ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="ai-engineering-control-plane-issues-box">
            <div className="flex items-center gap-2 font-semibold"><ShieldAlert className="h-4 w-4" /> Bloqueios actuais</div>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {config.readiness_issues.map((issue) => (<li key={issue}>{issue}</li>))}
            </ul>
          </div>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="space-y-3 rounded-sm border border-slate-200 p-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Settings backend</p>
              <p className="mt-1 text-sm text-slate-600">Sem default rígido no código. O backend falha de forma controlada se o modelo não estiver configurado.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Provider</Label>
                <Select value={settingsForm.provider} onValueChange={(value) => onSettingsChange('provider', value)} disabled={settingsDisabled}>
                  <SelectTrigger data-testid="ai-engineering-provider-select"><SelectValue placeholder="Seleccionar provider" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Modelo</Label>
                <Select value={settingsForm.model} onValueChange={(value) => onSettingsChange('model', value)} disabled={settingsDisabled || !settingsForm.provider}>
                  <SelectTrigger data-testid="ai-engineering-model-select"><SelectValue placeholder="Seleccionar modelo" /></SelectTrigger>
                  <SelectContent>
                    {(config?.supported_models || []).map((modelName) => (<SelectItem key={modelName} value={modelName}>{modelName}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ai-engineering-timeout-input" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Timeout (s)</Label>
                <Input id="ai-engineering-timeout-input" type="number" min="5" max="180" value={settingsForm.timeout_seconds} onChange={(event) => onSettingsChange('timeout_seconds', event.target.value)} disabled={settingsDisabled} data-testid="ai-engineering-timeout-input" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ai-engineering-retries-input" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Retries</Label>
                <Input id="ai-engineering-retries-input" type="number" min="0" max="5" value={settingsForm.retry_attempts} onChange={(event) => onSettingsChange('retry_attempts', event.target.value)} disabled={settingsDisabled} data-testid="ai-engineering-retries-input" />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="ai-engineering-fallback-toggle-row">
              <div>
                <p className="text-sm font-medium text-slate-900">Fallback controlado</p>
                <p className="text-xs text-slate-500">Capacidade preparada e governada; continua OFF por defeito nesta fase.</p>
              </div>
              <Switch checked={settingsForm.fallback_enabled} onCheckedChange={(checked) => onSettingsChange('fallback_enabled', checked)} disabled={settingsDisabled} data-testid="ai-engineering-fallback-switch" />
            </div>

            <Button className="h-9 rounded-sm bg-slate-900 text-xs font-semibold hover:bg-slate-800" onClick={onSaveSettings} disabled={settingsDisabled} data-testid="ai-engineering-save-settings-button">{savingSettings ? 'A guardar settings...' : 'Guardar settings backend'}</Button>
          </div>

          <div className="space-y-3 rounded-sm border border-slate-200 p-3">
            <div>
              <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500"><LockKeyhole className="h-3.5 w-3.5" /> Override administrativo seguro</p>
              <p className="mt-1 text-sm text-slate-600">A chave é enviada apenas para guardar, cifrada no backend e devolvida à UI só em forma mascarada.</p>
            </div>

            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="ai-engineering-credential-metadata-box">
              <p><span className="font-medium text-slate-900">Mascarado:</span> {config?.secure_override?.masked_value || 'não configurado'}</p>
              <p className="mt-1"><span className="font-medium text-slate-900">Actualizado por:</span> {config?.secure_override?.updated_by_name || '—'}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ai-engineering-credential-input" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Nova API key</Label>
              <Input id="ai-engineering-credential-input" type="password" value={credentialValue} onChange={(event) => onCredentialChange(event.target.value)} placeholder="Introduza a key para guardar de forma cifrada" disabled={credentialDisabled} data-testid="ai-engineering-credential-input" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button className="h-9 rounded-sm bg-slate-900 text-xs font-semibold hover:bg-slate-800" onClick={onSaveCredential} disabled={credentialDisabled || !credentialValue.trim()} data-testid="ai-engineering-save-credential-button">{savingCredential ? 'A guardar key...' : 'Guardar override seguro'}</Button>
              <Button variant="outline" className="h-9 rounded-sm text-xs" onClick={onClearCredential} disabled={credentialDisabled || !config?.secure_override?.configured} data-testid="ai-engineering-clear-credential-button">Remover override</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};